// ---------------------------------------------------------------------------
// FeedbackWidget — a small floating "Feedback" button + dialog so testers can
// report anything weird in one tap. Writes to the insert-only public.feedback
// table (see the SQL in the deploy notes). Captures the current page + browser
// so a vague "it broke" is still actionable. Fails gently — feedback is a
// nice-to-have, never a blocker.
//
// Hidden on the marketing pages ("/" and "/why") — it's for people *using*
// the app, not first-time visitors reading the pitch.
// ---------------------------------------------------------------------------

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";

const HIDE_ON = new Set(["/", "/why"]);

export const FeedbackWidget = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");

  // PLE-13 (Sep 2026): the one-time "You're one of the first to try this"
  // tester popup was removed — too many things greeted new users on first
  // open. The Feedback button itself is unchanged.

  // PRE-FILL the email for signed-in users (Morgan, Aug 4: "can we make the
  // feedback forms have them volunteer their email?" — the optional field
  // already existed, but an empty optional field gets skipped; a filled one
  // gets kept). Runs when the dialog opens; stays editable and erasable, and
  // clearing it mid-session isn't fought.
  useEffect(() => {
    if (!open) return;
    void supabase.auth.getSession().then(({ data }) => {
      const e = data.session?.user?.email;
      if (e) setEmail((cur) => cur || e);
    });
  }, [open]);

  if (HIDE_ON.has(location.pathname)) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === "loading") return;
    const body = message.trim();
    if (body.length < 2) { setState("error"); return; }
    setState("loading");
    // Emails the feedback straight to morgan@ via the submit-feedback edge
    // function (Resend). No table — it lands in the inbox.
    const { error } = await supabase.functions.invoke("submit-feedback", {
      body: {
        message: body,
        email: email.trim() || null,
        page: location.pathname,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : null,
      },
    });
    if (!error) {
      setState("done");
      window.setTimeout(() => {
        setOpen(false);
        setMessage(""); setEmail(""); setState("idle");
      }, 1400);
    } else {
      setState("error");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        className="fixed left-[calc(env(safe-area-inset-left)+8px)] bottom-[calc(env(safe-area-inset-bottom)+8px)] z-50 inline-flex items-center gap-1.5 rounded-full bg-foreground text-background text-xs font-medium pl-3 pr-3.5 py-2 shadow-elevated hover:brightness-110 transition-smooth"
      >
        <MessageSquare className="w-3.5 h-3.5" aria-hidden="true" />
        Feedback
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Send feedback</DialogTitle>
            <DialogDescription>
              Found something off, or have an idea? Tell me — it goes straight to Morgan.
            </DialogDescription>
          </DialogHeader>

          {state === "done" ? (
            <p className="text-[15px] text-primary font-medium py-4">
              Got it — thank you. This really helps.
            </p>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <Textarea
                value={message}
                onChange={(e) => { setMessage(e.target.value); if (state === "error") setState("idle"); }}
                placeholder="What happened, or what would make this better?"
                rows={4}
                autoFocus
                aria-label="Your feedback"
                className="resize-none"
              />
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email (optional — if you'd like a reply)"
                aria-label="Your email, optional"
                className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              {state === "error" && (
                <p className="text-sm text-destructive" role="alert">
                  {message.trim().length < 2
                    ? "Add a few words first, then send."
                    : "Couldn't send just now — please email support@pleasantroutes.com."}
                </p>
              )}
              <button
                type="submit"
                disabled={state === "loading"}
                className="w-full h-11 rounded-full bg-primary text-primary-foreground text-[15px] font-medium hover:brightness-110 transition-smooth disabled:opacity-60"
              >
                {state === "loading" ? "Sending…" : "Send feedback"}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
